import { motion } from "framer-motion";
import { AlertTriangle } from "lucide-react";
import { SectionHead } from "@/components/site/SectionHead";
import { QuoteButton, CallButton } from "@/components/site/Cta";
import { Reveal } from "@/components/site/Reveal";
import { TROUBLE_ISSUES } from "@/content/site";

const Scope = () => (
  <div className="glass relative overflow-hidden rounded-3xl p-6 sm:p-8" data-testid="trouble-scope">
    <div className="flex items-center justify-between">
      <p className="font-mono text-[10px] uppercase tracking-[0.24em] text-zinc-400">System Diagnostic</p>
      <span className="flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.2em] text-amber-400">
        <span className="h-1.5 w-1.5 rounded-full bg-amber-400 animate-pulse" /> Inspection Needed
      </span>
    </div>
    <svg viewBox="0 0 400 140" className="mt-6 w-full" aria-hidden="true">
      <defs><linearGradient id="tr-g" x1="0" x2="1"><stop offset="0" stopColor="#f5a623" stopOpacity="0.2" /><stop offset="1" stopColor="#f5a623" /></linearGradient></defs>
      {[20, 60, 100].map((y) => <line key={y} x1="0" x2="400" y1={y} y2={y} stroke="rgba(255,255,255,0.06)" />)}
      <motion.path d="M0 110 C40 108 60 40 100 38 S150 30 180 36 S210 100 230 104 S260 60 290 70 S330 118 360 112 S390 100 400 96"
        fill="none" stroke="url(#tr-g)" strokeWidth="2" initial={{ pathLength: 0 }} whileInView={{ pathLength: 1 }} viewport={{ once: true }} transition={{ duration: 2.4, ease: "easeInOut" }} />
      <circle cx="230" cy="104" r="5" fill="#f5a623" className="pin-pulse" />
      <circle cx="230" cy="104" r="3.5" fill="#f5a623" />
    </svg>
    <p className="mt-4 text-xs leading-relaxed text-zinc-400">Every system is different. Our technicians inspect and test your equipment on-site before recommending a solution.</p>
  </div>
);

export const Troubleshoot = () => (
  <section id="troubleshooting" data-testid="troubleshoot-section" className="relative bg-[#08090a] py-24 sm:py-32">
    <div className="mx-auto grid max-w-[1440px] gap-14 px-5 sm:px-8 lg:grid-cols-12 lg:px-12">
      <div className="lg:col-span-6">
        <SectionHead index="07" eyebrow="Troubleshooting" lines={["Solar system not", "performing like", "it should?"]}
          sub="If something doesn't look right, our team can inspect your system, test components and help identify what may be affecting performance." />
        <div className="mt-10 flex flex-col gap-3 sm:flex-row">
          <QuoteButton service="Troubleshooting" testId="trouble-cta">Schedule Solar Service</QuoteButton>
          <CallButton testId="trouble-call">Request Service</CallButton>
        </div>
        <Reveal className="mt-12"><Scope /></Reveal>
      </div>
      <ul className="lg:col-span-6 grid gap-3 sm:grid-cols-2 content-start" data-testid="trouble-issues">
        {TROUBLE_ISSUES.map((t, i) => (
          <motion.li key={t} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, amount: 0.1 }}
            transition={{ duration: 0.7, delay: i * 0.05 }} whileHover={{ x: 4 }}
            className="card-lux flex items-center gap-4 rounded-2xl p-5">
            <span className="grid h-10 w-10 shrink-0 place-items-center rounded-full border border-amber-400/30 bg-amber-400/10"><AlertTriangle className="h-4 w-4 text-amber-400" /></span>
            <span className="text-sm font-medium text-zinc-100">{t}</span>
          </motion.li>
        ))}
      </ul>
    </div>
  </section>
);
